import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { NotificationPreferences } from '@/types/user'

interface NotificationPreferencesModalProps {
  preferences?: NotificationPreferences
  isOpen: boolean
  onClose: () => void
  onSave: (preferences: NotificationPreferences) => Promise<void>
  isSubmitting?: boolean
}

const NotificationPreferencesModal: React.FC<NotificationPreferencesModalProps> = ({
  preferences,
  isOpen,
  onClose,
  onSave,
  isSubmitting = false,
}) => {
  const [emailNotifications, setEmailNotifications] = useState(
    preferences?.emailNotifications ?? true
  )
  const [inAppNotifications, setInAppNotifications] = useState(
    preferences?.inAppNotifications ?? true
  )

  if (!isOpen) return null

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSave({ emailNotifications, inAppNotifications })
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          {/* Close button */}
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Notification Preferences</h2>
            <p className="mt-1 text-sm text-gray-600">
              Choose how you'd like to be notified
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 mb-6">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={emailNotifications}
                  onChange={(e) => setEmailNotifications(e.target.checked)}
                  id="emailNotifications"
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
                <label htmlFor="emailNotifications" className="text-sm font-medium text-gray-700">
                  Email notifications
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={inAppNotifications}
                  onChange={(e) => setInAppNotifications(e.target.checked)}
                  id="inAppNotifications"
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
                <label htmlFor="inAppNotifications" className="text-sm font-medium text-gray-700">
                  In-app notifications
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Save
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default NotificationPreferencesModal
